import { PLAN } from '../types/plan.js';
import { BRAND } from '../types/brand.js';
import { COLOR } from '../types/color.js';
import { COMPONENT } from '../types/component.js';
import { COST_CENTER } from '../types/cost-center.js';
import { DISTRIBUTION_CENTER } from '../types/distribution-center.js';
import { FACTORY } from '../types/factory.js';
import { LOT } from '../types/lot.js';
import { MACHINE } from '../types/machine.js';
import { STOCK_LOCATION } from '../types/stock-location.js';
import { MACHINE_GROUP } from '../types/machine-group.js';
import { OPERATION } from '../types/operation.js';
import { SALES_ORDER } from '../types/sales-order.js';
import { SALES_ORDER_ITEM } from '../types/sales-order-item.js';
import { SALES_ORDER_ITEM_BREAKDOWN } from '../types/sales-order-item-breakdown.js';
import { PRODUCTION_ORDER } from '../types/production-order.js';
import { PRODUCTION_ORDER_BREAKDOWN } from '../types/production-order-breakdown.js';
import { PRODUCTION_RUN } from '../types/production-run.js';
import { PRODUCTION_STOP } from '../types/production-stop.js';
import { PRODUCTION_STOP_REASON } from '../types/production-stop-reason.js';
import { PRODUCTION_TICKET } from '../types/production-ticket.js';
import { PRODUCTION_ENTRY } from '../types/production-entry.js';
import { ORGANIZATION_DATA } from '../types/organization.js';
import { PARTY } from '../types/party.js';
import { PROCESS } from '../types/process.js';
import { PRODUCT } from '../types/product.js';
import { PRODUCT_GROUP } from '../types/product-group.js';
import { PRODUCT_VARIANT } from '../types/product-variant.js';
import { PROFILE_DATA } from '../types/profile.js';
import { PURCHASE_ORDER } from '../types/purchase-order.js';
import { PURCHASE_ORDER_ITEM } from '../types/purchase-order-item.js';
import { PURCHASE_ORDER_ITEM_BREAKDOWN } from '../types/purchase-order-item-breakdown.js';
import { INVOICE } from '../types/invoice.js';
import { INVOICE_ITEM } from '../types/invoice-item.js';
import { INVOICE_IMPORT } from '../types/invoice-import.js';
import { SUPPLIER_PRODUCT_MAP } from '../types/supplier-product-map.js';
import { SECTOR } from '../types/sector.js';
import { TAG } from '../types/tag.js';
import { TECHNICAL_SHEET } from '../types/technical-sheet.js';
import { TECHNICAL_SHEET_MATERIAL } from '../types/technical-sheet-material.js';
import { TECHNICAL_SHEET_OVERRIDE } from '../types/technical-sheet-override.js';
import { TECHNICAL_SHEET_OVERRIDE_INPUT } from '../types/technical-sheet-override-input.js';
import { TECHNICAL_SHEET_OVERRIDE_PROCESS } from '../types/technical-sheet-override-process.js';
import { TECHNICAL_SHEET_ROUTE } from '../types/technical-sheet-route.js';
import { TECHNICAL_SHEET_ROUTE_MACHINE } from '../types/technical-sheet-route-machine.js';
import { OPERATION_MACHINE } from '../types/operation-machine.js';
import { PRODUCTION_ORDER_MATERIAL } from '../types/production-order-material.js';
import { PRODUCTION_ORDER_STEP } from '../types/production-order-step.js';
import { SIZE_OCCURRENCE } from '../types/size-occurrence.js';
import { USER_DATA, ORGANIZATION_USER_ORG_DATA } from '../types/user.js';
import { VARIATION_OPTION } from '../types/variation-option.js';
import { WORK_CALENDAR } from '../types/work-calendar.js';
import { TICKET_AUTOMATION_RULE, TICKET_WORKFLOW_STAGE, TICKET_WORKSPACE, } from '../types/ticket-workspace.js';
import { WORKSPACE_TICKET } from '../types/workspace-ticket.js';
import { SAVED_SELECTION } from '../types/saved-selection.js';
/** Field names stamped by `auditService` on create/update. */
export const AUDIT_FIELD_NAMES = [
    'inclusionDate',
    'lastModifiedDate',
    'inclusionUserId',
    'lastModifiedUserId',
];
/**
 * Shared audit field descriptors — mirrors `Auditable`.
 * Always persisted (no groups). Spread at the end of auditable entity `fields`.
 */
export const AUDIT_FIELD_DESCRIPTORS = [
    { name: 'inclusionDate', label: 'Data de criação', type: 'date', required: false },
    { name: 'lastModifiedDate', label: 'Última alteração', type: 'date', required: false },
    { name: 'inclusionUserId', label: 'Criado por', type: 'string', required: false },
    { name: 'lastModifiedUserId', label: 'Alterado por', type: 'string', required: false },
];
/**
 * FK declarations for audit user fields — spread into entity `foreignKeys`.
 *
 * `expand: false` (request 0167 §11): the relation exists to **label** the id with the
 * user's name (resolved by the System API, not by the client repository). There is no
 * join — the row carries only the uid — so expanding `UserData` into nested columns
 * would offer `Alterado por › Email` and friends, which can never be filled.
 */
export const AUDIT_FOREIGN_KEYS = [
    {
        fieldName: 'inclusionUserId',
        targetEntity: USER_DATA,
        relationship: 'many-to-one',
        displayField: 'name',
        expand: false,
    },
    {
        fieldName: 'lastModifiedUserId',
        targetEntity: USER_DATA,
        relationship: 'many-to-one',
        displayField: 'name',
        expand: false,
    },
];
/**
 * Entities whose services call `auditService.stampOnCreate` / `stampOnUpdate`
 * (directly or via `OrgScopedCrudService` / `ChildCrudService` with default
 * `auditable: true`).
 */
export const AUDITABLE_ENTITY_NAMES = [
    BRAND,
    COLOR,
    COMPONENT,
    COST_CENTER,
    DISTRIBUTION_CENTER,
    FACTORY,
    LOT,
    MACHINE,
    STOCK_LOCATION,
    MACHINE_GROUP,
    OPERATION,
    OPERATION_MACHINE,
    SALES_ORDER,
    SALES_ORDER_ITEM,
    SALES_ORDER_ITEM_BREAKDOWN,
    PRODUCTION_ORDER,
    PRODUCTION_ORDER_BREAKDOWN,
    PRODUCTION_ORDER_MATERIAL,
    PRODUCTION_ORDER_STEP,
    PRODUCTION_RUN,
    PRODUCTION_STOP,
    PRODUCTION_STOP_REASON,
    PRODUCTION_TICKET,
    PRODUCTION_ENTRY,
    ORGANIZATION_DATA,
    ORGANIZATION_USER_ORG_DATA,
    PARTY,
    PLAN,
    PROCESS,
    PRODUCT,
    PRODUCT_GROUP,
    PRODUCT_VARIANT,
    PROFILE_DATA,
    PURCHASE_ORDER,
    PURCHASE_ORDER_ITEM,
    PURCHASE_ORDER_ITEM_BREAKDOWN,
    INVOICE,
    INVOICE_ITEM,
    INVOICE_IMPORT,
    SUPPLIER_PRODUCT_MAP,
    SECTOR,
    SIZE_OCCURRENCE,
    TAG,
    TECHNICAL_SHEET,
    TECHNICAL_SHEET_MATERIAL,
    TECHNICAL_SHEET_OVERRIDE,
    TECHNICAL_SHEET_OVERRIDE_INPUT,
    TECHNICAL_SHEET_OVERRIDE_PROCESS,
    TECHNICAL_SHEET_ROUTE,
    TECHNICAL_SHEET_ROUTE_MACHINE,
    USER_DATA,
    VARIATION_OPTION,
    WORK_CALENDAR,
    TICKET_WORKSPACE,
    TICKET_WORKFLOW_STAGE,
    TICKET_AUTOMATION_RULE,
    WORKSPACE_TICKET,
    SAVED_SELECTION,
];
//# sourceMappingURL=_auditFields.js.map